import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@mantine/core';
import {
  IconArrowLeft,
  IconBolt,
  IconBrain,
  IconDumbbell,
  IconLeaf,
  IconMusic,
  IconPlayerPlay,
  IconRun,
  IconSearch,
  IconSettings,
} from '@tabler/icons-react';
import toast from 'react-hot-toast';

import mascots from '@assets/images/tc-mascots-headphones.png';
import {
  AppleMusicMiniPlayer,
  useAppleMusicPlayer,
} from '@components/Athlete/AppleMusicPlayer';
import {
  fetchAppleMusicHome,
  fetchAppleMusicPlaylist,
  fetchFeaturedWorkoutPlaylists,
  searchAppleMusic,
  setActiveWorkoutPlaylist,
} from '@util/appleMusicSession';
import { toErrorMessage } from '@util/toErrorMessage';

import classes from './Music.module.css';

const FEATURED_PREVIEW = 10;

const FILTERS = [
  { value: 'training', label: 'Training' },
  { value: 'library', label: 'My Playlists' },
];

const SEARCH_TYPES = [
  { value: 'playlist,track,album,artist', label: 'All' },
  { value: 'playlist', label: 'Playlists' },
  { value: 'track', label: 'Tracks' },
  { value: 'album', label: 'Albums' },
  { value: 'artist', label: 'Artists' },
];

const CATEGORY_CARDS = [
  {
    key: 'strength',
    filter: 'training',
    title: 'Strength',
    copy: 'Heavy beats for heavy lifts.',
    Icon: IconDumbbell,
  },
  {
    key: 'cardio',
    filter: 'energy',
    title: 'Cardio',
    copy: 'Keep the pace high.',
    Icon: IconRun,
  },
  {
    key: 'hiit',
    filter: 'energy',
    title: 'HIIT',
    copy: 'Intervals that hit.',
    Icon: IconBolt,
  },
  {
    key: 'focus',
    filter: 'focus',
    title: 'Focus',
    copy: 'Lock in and move.',
    Icon: IconBrain,
  },
  {
    key: 'recovery',
    filter: 'recovery',
    title: 'Recovery',
    copy: 'Reset and recharge.',
    Icon: IconLeaf,
  },
];

function formatDuration(ms) {
  const total = Math.floor(Number(ms || 0) / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function Cover({ src, alt, className }) {
  if (!src) {
    return (
      <div className={`${classes.coverFallback} ${className || ''}`.trim()}>
        <IconMusic size={28} stroke={1.75} />
      </div>
    );
  }
  return <img src={src} alt={alt || ''} className={className} />;
}

function playlistIdOf(playlist) {
  return playlist?.id || playlist?.playlistId || null;
}

export default function AthleteMusic() {
  const {
    connected,
    connection,
    refreshConnection,
    authorizeAndConnect,
    disconnectAndForget,
    authorized,
    configured,
    playPlaylist,
    playTrack,
    ready,
  } = useAppleMusicPlayer();

  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [filter, setFilter] = useState('training');
  const [home, setHome] = useState(null);
  const [homeLoading, setHomeLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState(SEARCH_TYPES[0].value);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistLoading, setPlaylistLoading] = useState(false);
  const [featuredExpanded, setFeaturedExpanded] = useState(false);
  const [featuredExtra, setFeaturedExtra] = useState([]);
  const [featuredLoadingMore, setFeaturedLoadingMore] = useState(false);
  const [featuredHasMore, setFeaturedHasMore] = useState(false);
  const [featuredSearchOffset, setFeaturedSearchOffset] = useState(25);
  const bootstrappedRef = useRef(false);

  function loadHome() {
    setHomeLoading(true);
    return fetchAppleMusicHome()
      .then((payload) => {
        setHome(payload || {});
        setFeaturedExpanded(false);
        setFeaturedExtra([]);
        setFeaturedSearchOffset(25);
        setFeaturedHasMore(Boolean(payload?.featured?.next));
        return payload;
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        setHomeLoading(false);
      });
  }

  useEffect(() => {
    setLoading(true);
    refreshConnection()
      .then((payload) => {
        if (payload?.connected) {
          return loadHome();
        }
        setHome(null);
        setSelectedPlaylist(null);
        setResults(null);
        return null;
      })
      .catch((error) => {
        console.error('Apple Music connection error', error);
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
        bootstrappedRef.current = true;
      });
  }, []);

  useEffect(() => {
    if (!bootstrappedRef.current) return undefined;
    if (connected) {
      loadHome();
    } else {
      setHome(null);
      setSelectedPlaylist(null);
      setResults(null);
    }
  }, [connected]);

  const hasResults = useMemo(() => {
    if (!results) return false;
    return (
      (results.playlists || []).length > 0 ||
      (results.tracks || []).length > 0 ||
      (results.albums || []).length > 0 ||
      (results.artists || []).length > 0
    );
  }, [results]);

  const featuredPage = home?.featured || {
    data: [],
    total: 0,
    next: false,
  };
  const featuredAll = useMemo(() => {
    const base = featuredPage.data || [];
    const seen = new Set(base.map((item) => playlistIdOf(item)).filter(Boolean));
    const extras = featuredExtra.filter((item) => {
      const id = playlistIdOf(item);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return [...base, ...extras];
  }, [featuredPage.data, featuredExtra]);

  const featuredVisible = featuredExpanded
    ? featuredAll
    : featuredAll.slice(0, FEATURED_PREVIEW);

  const sections = home?.sections || [];
  const library = home?.library?.data || [];
  const recentlyPlayed = home?.recently_played || [];

  const visibleSections = useMemo(() => {
    if (filter === 'library') return [];
    const scoped =
      filter === 'training'
        ? sections
        : sections.filter((section) => section.filter === filter);
    return scoped.filter((section) => (section.playlists || []).length > 0);
  }, [filter, sections]);

  function onConnect() {
    setConnecting(true);
    authorizeAndConnect()
      .then(() => {
        toast.success('Apple Music connected');
        return refreshConnection().then((payload) => {
          if (payload?.connected) return loadHome();
          return null;
        });
      })
      .catch((error) => {
        console.error('Apple Music connection error', error);
      })
      .finally(() => {
        setConnecting(false);
      });
  }

  function onDisconnect() {
    setDisconnecting(true);
    disconnectAndForget()
      .then(() => {
        toast.success('Apple Music disconnected');
        setSettingsOpen(false);
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        setDisconnecting(false);
      });
  }

  function onSearch(event) {
    event?.preventDefault?.();
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearching(true);
    setSelectedPlaylist(null);
    searchAppleMusic({ q: trimmed, type: searchType })
      .then((payload) => {
        setResults(payload || {});
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        setSearching(false);
      });
  }

  function openPlaylist(playlistId) {
    if (!playlistId) return;
    setPlaylistLoading(true);
    setSelectedPlaylist({ id: playlistId, name: 'Loading…', tracks: [] });
    fetchAppleMusicPlaylist(playlistId)
      .then((payload) => {
        if (payload && payload.can_list_items === false) {
          setSelectedPlaylist(null);
          playAndUse(payload);
          return;
        }
        setSelectedPlaylist(payload);
      })
      .catch((error) => {
        setSelectedPlaylist(null);
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        setPlaylistLoading(false);
      });
  }

  function closePlaylistModal() {
    setSelectedPlaylist(null);
  }

  function useInWorkout(playlist) {
    const payload = setActiveWorkoutPlaylist({
      playlistId: playlist.id || playlist.playlistId,
      name: playlist.name || playlist.catalog_name,
      imageUrl: playlist.image_url || playlist.imageUrl,
      externalUrl: playlist.external_url || playlist.externalUrl,
      uri: playlist.uri,
    });
    if (payload) {
      toast.success(`“${payload.name}” ready for workout`);
    }
  }

  function playAndUse(playlist) {
    useInWorkout(playlist);
    playPlaylist(playlist).catch(() => {});
  }

  function loadMoreFeatured() {
    setFeaturedLoadingMore(true);
    fetchFeaturedWorkoutPlaylists({ limit: 25, offset: featuredSearchOffset })
      .then((page) => {
        setFeaturedExtra((prev) => [...prev, ...(page.data || [])]);
        setFeaturedSearchOffset((current) => current + 25);
        setFeaturedHasMore(Boolean(page.next));
      })
      .catch((error) => {
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        setFeaturedLoadingMore(false);
      });
  }

  // Wait for MusicKit to finish booting before judging authorization, or a
  // connected-and-authorized athlete gets a flash of the connect hero on load.
  if (loading || (connected && !configured)) {
    return (
      <div className={classes.page}>
        <p className={classes.muted}>Loading soundtrack…</p>
      </div>
    );
  }

  // Linked on the account is not the same as authorized in this browser, and
  // without MusicKit authorization nothing here can actually play.
  if (!connected || !authorized) {
    const needsBrowserAuth = connected && !authorized;
    return (
      <div className={classes.heroPage}>
        <div className={classes.hero}>
          <img
            src={mascots}
            alt="T&C's Gymnasium"
            className={classes.heroLogo}
          />
          <p className={classes.eyebrow}>Soundtrack</p>
          <h1 className={classes.heroTitle}>Music for the work.</h1>
          <p className={classes.heroCopy}>
            {needsBrowserAuth
              ? 'Apple Music is linked to your account. Authorize it in this browser to browse and play playlists in Training Tracker.'
              : 'Connect Apple Music to play playlists in Training Tracker — browse, queue, and control playback without leaving the gym floor.'}
          </p>
          <button
            type="button"
            className={classes.primaryBtn}
            onClick={onConnect}
            disabled={connecting}
          >
            {connecting
              ? needsBrowserAuth
                ? 'Authorizing…'
                : 'Connecting…'
              : needsBrowserAuth
                ? 'Enable Apple Music'
                : 'Connect Apple Music'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <header className={classes.homeHeader}>
        <div className={classes.brandBlock}>
          <div>
            <p className={classes.eyebrow}>Music</p>
            <h1 className={classes.homeTitle}>Train Harder</h1>
            <p className={classes.homeSub}>
              Curated playlists to fuel every part of the grind.
            </p>
          </div>
        </div>
        <div className={classes.headerActions}>
          <span className={classes.connectedPill}>
            <span className={classes.connectedDot} aria-hidden />
            Connected to Apple Music
          </span>
          <button
            type="button"
            className={classes.settingsBtn}
            onClick={() => setSettingsOpen((value) => !value)}
            aria-label="Music settings"
          >
            <IconSettings size={16} stroke={2} />
            Music Settings
          </button>
        </div>
      </header>

      {settingsOpen ? (
        <div className={classes.settingsPanel}>
          {connection?.display_name ? (
            <p className={classes.muted}>
              Connected as {connection.display_name}
              {authorized && ready
                ? ' · Player ready'
                : authorized
                  ? ' · Starting player…'
                  : ' · Tap Enable to authorize this browser'}
            </p>
          ) : null}
          <button
            type="button"
            className={classes.ghostBtn}
            onClick={onDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect Apple Music'}
          </button>
          <button
            type="button"
            className={classes.ghostBtn}
            onClick={onConnect}
            disabled={connecting}
          >
            {authorized ? 'Reconnect' : 'Enable on this browser'}
          </button>
        </div>
      ) : null}

      <form className={classes.searchForm} onSubmit={onSearch}>
        <div className={classes.searchField}>
          <IconSearch size={18} stroke={2} className={classes.searchIcon} />
          <input
            className={classes.searchInput}
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search Apple Music (artists, songs, playlists…)"
            aria-label="Search Apple Music"
          />
          {query ? (
            <button
              type="button"
              className={classes.searchClear}
              onClick={() => setQuery('')}
            >
              Clear
            </button>
          ) : null}
        </div>
        <div className={classes.typeChips}>
          {SEARCH_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              className={`${classes.chip} ${
                searchType === type.value ? classes.chipActive : ''
              }`}
              onClick={() => setSearchType(type.value)}
            >
              {type.label}
            </button>
          ))}
        </div>
      </form>

      <div className={classes.tabRow}>
        {FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            className={`${classes.tabChip} ${
              filter === item.value ? classes.tabChipActive : ''
            }`}
            onClick={() => {
              setFilter(item.value);
              setResults(null);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {hasResults ? (
        <section className={classes.section}>
          <div className={classes.sectionHead}>
            <p className={classes.sectionLabel}>Results</p>
            <button
              type="button"
              className={classes.backBtn}
              onClick={() => {
                setQuery('');
                setResults(null);
              }}
            >
              <IconArrowLeft size={16} stroke={2} />
              Back to Music
            </button>
          </div>
          {(results.playlists || []).length > 0 ? (
            <div className={classes.coverRail}>
              {results.playlists.map((playlist) => (
                <button
                  key={playlist.id}
                  type="button"
                  className={classes.coverCard}
                  onClick={() => openPlaylist(playlist.id)}
                >
                  <Cover
                    src={playlist.image_url}
                    alt={playlist.name}
                    className={classes.coverImage}
                  />
                  <span className={classes.coverName}>{playlist.name}</span>
                </button>
              ))}
            </div>
          ) : null}
          {(results.tracks || []).length > 0 ? (
            <div className={classes.trackList}>
              {results.tracks.map((track, index) => (
                <button
                  key={track.id || `${track.name}-${index}`}
                  type="button"
                  className={classes.trackRowBtn}
                  onClick={() =>
                    // The provider already toasts playback failures.
                    playTrack(track).catch(() => {})
                  }
                >
                  <Cover
                    src={track.image_url}
                    alt=""
                    className={classes.trackThumb}
                  />
                  <div className={classes.trackMain}>
                    <p className={classes.trackName}>{track.name}</p>
                    <p className={classes.trackArtists}>
                      {(track.artists || [])
                        .map((artist) => artist.name)
                        .join(', ')}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : null}
          {searching ? <p className={classes.muted}>Searching…</p> : null}
        </section>
      ) : null}

      {!hasResults && filter !== 'library' ? (
        <>
          <section className={classes.section}>
            <div className={classes.sectionHead}>
              <p className={classes.sectionLabel}>Featured Playlists</p>
              {featuredAll.length > FEATURED_PREVIEW || featuredHasMore ? (
                <button
                  type="button"
                  className={classes.viewAllBtn}
                  onClick={() => {
                    if (!featuredExpanded) {
                      setFeaturedExpanded(true);
                      return;
                    }
                    if (featuredHasMore) {
                      loadMoreFeatured();
                    } else {
                      setFeaturedExpanded(false);
                    }
                  }}
                  disabled={featuredLoadingMore}
                >
                  {featuredLoadingMore
                    ? 'Loading…'
                    : !featuredExpanded
                      ? 'View all →'
                      : featuredHasMore
                        ? 'Load more →'
                        : 'Show less'}
                </button>
              ) : null}
            </div>
            {homeLoading && featuredVisible.length === 0 ? (
              <p className={classes.muted}>Loading playlists…</p>
            ) : featuredVisible.length === 0 ? (
              <p className={classes.muted}>No featured playlists yet.</p>
            ) : (
              <div
                className={
                  featuredExpanded ? classes.featuredGrid : classes.featuredRail
                }
              >
                {featuredVisible.map((playlist) => (
                  <button
                    key={playlist.catalog_key || playlist.id}
                    type="button"
                    className={classes.featuredCard}
                    onClick={() => openPlaylist(playlistIdOf(playlist))}
                  >
                    <div className={classes.featuredArtWrap}>
                      <Cover
                        src={playlist.image_url}
                        alt={playlist.catalog_name || playlist.name}
                        className={classes.featuredArt}
                      />
                    </div>
                    <span className={classes.coverName}>
                      {playlist.catalog_name || playlist.name}
                    </span>
                    <span className={classes.coverMeta}>
                      {playlist.tagline ||
                        playlist.owner_name ||
                        'Apple Music'}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {filter === 'training' ? (
            <section className={classes.section}>
              <div className={classes.sectionHead}>
                <p className={classes.sectionLabel}>Training Categories</p>
              </div>
              <div className={classes.categoryRail}>
                {CATEGORY_CARDS.map((card) => {
                  const Icon = card.Icon;
                  return (
                    <button
                      key={card.key}
                      type="button"
                      className={classes.categoryCard}
                      onClick={() => {
                        setFilter(card.filter);
                        setResults(null);
                      }}
                    >
                      <Icon size={28} stroke={1.75} />
                      <span className={classes.categoryTitle}>{card.title}</span>
                      <span className={classes.categoryCopy}>{card.copy}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {homeLoading && visibleSections.length > 0 ? (
            <p className={classes.muted}>Loading playlists…</p>
          ) : (
            visibleSections.map((section) => (
              <section key={section.key} className={classes.section}>
                <div className={classes.sectionHead}>
                  <div>
                    <p className={classes.sectionLabel}>{section.name}</p>
                    {section.tagline ? (
                      <p className={classes.sectionTagline}>
                        {section.tagline}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className={classes.coverRail}>
                  {section.playlists.map((playlist) => (
                    <button
                      key={playlist.catalog_key || playlist.id}
                      type="button"
                      className={classes.coverCard}
                      onClick={() => openPlaylist(playlistIdOf(playlist))}
                    >
                      <Cover
                        src={playlist.image_url}
                        alt={playlist.catalog_name || playlist.name}
                        className={classes.coverImage}
                      />
                      <span className={classes.coverName}>
                        {playlist.catalog_name || playlist.name}
                      </span>
                      <span className={classes.coverMeta}>
                        {playlist.tagline ||
                          playlist.owner_name ||
                          'Playlist'}
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ))
          )}

          {filter === 'training' && recentlyPlayed.length > 0 ? (
            <section className={classes.section}>
              <div className={classes.sectionHead}>
                <p className={classes.sectionLabel}>Recently Played</p>
              </div>
              <div className={classes.recentGrid}>
                {recentlyPlayed.slice(0, 10).map((track, index) => (
                  <button
                    key={`${track.id}-${track.played_at || index}`}
                    type="button"
                    className={classes.recentRow}
                    onClick={() => {
                      if (!authorized) {
                        toast.error(
                          'Enable Apple Music on this browser first',
                        );
                        return;
                      }
                      playTrack(track).catch(() => {});
                    }}
                  >
                    <Cover
                      src={track.image_url}
                      alt=""
                      className={classes.trackThumb}
                    />
                    <div className={classes.trackMain}>
                      <p className={classes.trackName}>{track.name}</p>
                      <p className={classes.trackArtists}>
                        {(track.artists || [])
                          .map((artist) => artist.name)
                          .join(', ')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}

      {!hasResults && filter === 'library' ? (
        <section className={classes.section}>
          <p className={classes.sectionLabel}>My Playlists</p>
          {homeLoading ? (
            <p className={classes.muted}>Loading playlists…</p>
          ) : library.length === 0 ? (
            <p className={classes.muted}>
              No playlists found on this account.
            </p>
          ) : (
            <div className={classes.coverRail}>
              {library.map((playlist) => (
                <button
                  key={playlist.id}
                  type="button"
                  className={classes.coverCard}
                  onClick={() => openPlaylist(playlist.id)}
                >
                  <Cover
                    src={playlist.image_url}
                    alt={playlist.name}
                    className={classes.coverImage}
                  />
                  <span className={classes.coverName}>{playlist.name}</span>
                  <span className={classes.coverMeta}>
                    {playlist.track_count != null
                      ? `${playlist.track_count} tracks`
                      : playlist.owner_name || 'Playlist'}
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      ) : null}

      <Modal
        opened={Boolean(selectedPlaylist)}
        onClose={closePlaylistModal}
        title={selectedPlaylist?.name || 'Playlist'}
        size="lg"
        centered
        radius="md"
        classNames={{
          content: classes.modalContent,
          header: classes.modalHeader,
          body: classes.modalBody,
          title: classes.modalTitle,
        }}
      >
        {selectedPlaylist ? (
          <div className={classes.modalInner}>
            <div className={classes.detailHero}>
              <Cover
                src={selectedPlaylist.image_url}
                alt={selectedPlaylist.name}
                className={classes.detailCover}
              />
              <div className={classes.detailMeta}>
                <p className={classes.eyebrow}>Playlist</p>
                <h2 className={classes.detailTitle}>{selectedPlaylist.name}</h2>
                <p className={classes.detailSub}>
                  {[
                    selectedPlaylist.owner_name,
                    selectedPlaylist.track_count != null
                      ? `${selectedPlaylist.track_count} tracks`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
                <div className={classes.detailActions}>
                  <button
                    type="button"
                    className={classes.primaryBtn}
                    onClick={() => playAndUse(selectedPlaylist)}
                    disabled={playlistLoading}
                  >
                    <IconPlayerPlay size={16} stroke={2} />
                    Play
                  </button>
                  <button
                    type="button"
                    className={classes.ghostBtn}
                    onClick={() => useInWorkout(selectedPlaylist)}
                  >
                    Use in workout
                  </button>
                </div>
              </div>
            </div>
            <div className={classes.trackList}>
              {playlistLoading && !(selectedPlaylist.tracks || []).length ? (
                <p className={classes.muted}>Loading tracks…</p>
              ) : null}
              {(selectedPlaylist.tracks || []).map((track, index) => (
                <button
                  key={track.id || `${track.name}-${index}`}
                  type="button"
                  className={classes.trackRowBtn}
                  onClick={() =>
                    playTrack(track, selectedPlaylist).catch(() => {})
                  }
                >
                  <Cover
                    src={track.image_url}
                    alt=""
                    className={classes.trackThumb}
                  />
                  <div className={classes.trackMain}>
                    <p className={classes.trackName}>{track.name}</p>
                    <p className={classes.trackArtists}>
                      {(track.artists || [])
                        .map((artist) => artist.name)
                        .join(', ')}
                    </p>
                  </div>
                  <span className={classes.trackDuration}>
                    {formatDuration(track.duration_ms)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </Modal>

      <div className={classes.dockSlot} aria-hidden />
      <div className={classes.dockFixed}>
        <AppleMusicMiniPlayer mode="docked" />
      </div>
    </div>
  );
}
